import { useQuery } from '@tanstack/react-query';
import { useIntercom } from "react-use-intercom";
import { Button, Input, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { CopyButton } from "@/components/copyButton";
import { getKnowledgeBasePrivateLinkSetting } from "@/helpers/apis";
import { API_URL } from "@/config";
import { getTenant } from "@/helpers/tenant";

interface PrivateLinkSectionProps {
  publicId?: string;
  onCopy?: () => void;
}

export default function PrivateLinkSection({ publicId, onCopy }: PrivateLinkSectionProps) {
  const { show } = useIntercom();

  const { data: privateLinkSetting } = useQuery({
    queryKey: ["knowledgeBasePrivateLinkSetting"],
    queryFn: () => getKnowledgeBasePrivateLinkSetting(),
  });

  const isPrivateLinkEnabled = privateLinkSetting?.config?.enabled;
  const port = privateLinkSetting?.config?.port;
  const link = isPrivateLinkEnabled
    ? `http://localhost:${port ?? 4000}/kb/${publicId}`
    : `${API_URL}/knowledge_bases/public/${publicId}?tenant=${getTenant()}`;

  const handleRequestPrivateLink = () => {
    show();
  };

  return (
    <>
      <div className="al-border-t al-pt-4" />
      <Stack direction="column" gap="sm">
        <Stack direction="row" align="center" className="al-gap-2">
          <Typography variant="body" size="sm" weight="medium" className="al-flex-shrink-0">
            Link:
          </Typography>
          <Input value={link} readOnly className="al-flex-1 al-max-w-md" />
          <span onClick={onCopy} role="presentation">
            <CopyButton value={link} />
          </span>
        </Stack>
        <Typography variant="body" size="sm" className="al-text-gray-600">
          You can use this knowledge source directly as a link in your IDE of choice.{" "}
          <Button variant="link" asChild className="al-p-0 al-h-auto">
            <a
              href="https://datamates-docs.myaltimate.com/user-guide/components/knowledgehub/"
              target="_blank"
              rel="noreferrer"
            >
              Read more.
            </a>
          </Button>
        </Typography>
        {isPrivateLinkEnabled ? (
          <Typography variant="body" size="sm" className="al-text-green-600">
            This knowledge document is protected with private link access.
          </Typography>
        ) : (
          <Typography variant="body" size="sm" className="al-text-red-600">
            This knowledge document will be accessible to anyone with the link.{" "}
            <Button variant="link" onClick={handleRequestPrivateLink} className="al-p-0 al-h-auto">
              Request private link
            </Button>
          </Typography>
        )}
      </Stack>
    </>
  );
}
